import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import TablePagination from "../../shared/TablePagination";
import useAutoAdvancePagination from "../../shared/useAutoAdvancePagination";
import ExportLoader from "../../shared/ExportLoader";
import useReportExport from "../../shared/useReportExport";
import jsPDF from "jspdf";
import "jspdf-autotable";
import ActionToolbar from "../../NavBars/ActionToolbar";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";
import {
  FiPrinter,
  FiFileText,
  FiFile,
  FiLayout,
  FiLogOut,
} from "react-icons/fi";
import axios from "axios";
import { useDbContext } from "../../../context/DbContext";

function ResizableTh({ columnKey, width, onResize, children, className }) {
  const handleMouseDown = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const thElement = e.currentTarget.parentElement;
    const startWidth = width || thElement.getBoundingClientRect().width || 100;

    const onMouseMove = (moveEvent) => {
      const diff = moveEvent.clientX - startX;
      const newWidth = Math.max(50, startWidth + diff);
      onResize(columnKey, newWidth);
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  return (
    <th
      className={`relative ${className}`}
      style={{
        width,
        minWidth: width,
        maxWidth: width,
      }}
    >
      <div className="w-full h-full flex items-center overflow-hidden whitespace-nowrap text-ellipsis">
        {children}
      </div>
      <div
        className="absolute right-0 top-0 h-full w-2 cursor-col-resize z-10 hover:bg-white/20"
        onMouseDown={handleMouseDown}
      />
    </th>
  );
}

const GroupListReport = ({ payload, onClose, zIndex }) => {
  const defaultHeaders = ["Particulars", "Address", "Op. Balance", "Debit", "Credit", "Balance", "Dr/Cr"];
  const [headers, setHeaders] = useState(defaultHeaders);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [globalSearch, setGlobalSearch] = useState("");
  const tableRef = useRef(null);
  const { dbCode } = useDbContext();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  const [columnWidths, setColumnWidths] = useState({
    name: 180,
    credit: 120,
    balance: 120,
    drCr: 60,
  });

  useEffect(() => {
    if (!payload || !dbCode) return;

    const fetchReportData = async () => {
      setLoading(true);
      try {
        const response = await axios.post(
          `${apiBaseUrl}/accountreports/grouplist/${dbCode}`,
          payload,
        );
        if (response.data && response.data.success) {
          const rawData = response.data.data || [];
          if (rawData.length > 0) {
            const resultHeaders = Object.keys(rawData[0]).filter(k => k !== 'RowID' && k !== 'LTDate' && k !== 'LedCode');
            setHeaders(resultHeaders);
            setData(rawData);
          } else {
            setHeaders(defaultHeaders);
            setData([]);
          }
        } else {
          console.error("Failed to fetch report data:", response.data?.message);
        }
      } catch (err) {
        console.error("Error fetching report data:", err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [payload, dbCode, apiBaseUrl]);

  const parseMoney = (val) => {
    if (!val) return 0;
    const clean = String(val).replace(/,/g, "");
    const num = parseFloat(clean);
    return isNaN(num) ? 0 : num;
  };

  const formatMoney = (num) => {
    return Number(num).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const filteredData = data.filter((row) => {
    return (
      globalSearch === "" ||
      Object.values(row).some((val) =>
        String(val || "")
          .toLowerCase()
          .includes(globalSearch.toLowerCase()),
      )
    );
  });

  const {
    currentPage,
    pageSize,
    pageSizeOptions,
    totalPages,
    totalRows,
    displayedRows,
    selectedIndex,
    setSelectedIndex,
    tableContainerRef,
    handleScroll,
    handleWheel,
    handleKeyDown,
    handlePageChange,
    handlePageSizeChange,
    autoAdvance,
    toggleAutoAdvance,
    isFlipping,
  } = useAutoAdvancePagination({
    items: filteredData,
    initialPageSize: 100,
    pageSizeOptions: [50, 100, 250, 500],
    initialAutoAdvance: true,
  });

  const totals = {};
  headers.forEach(h => {
    const isNum = ['OPBalance', 'Debit', 'Credit', 'Balance', 'Payment', 'Receipt', 'GroupAmount', 'Ledamount', 'Amount'].includes(h);
    if (isNum) {
      totals[h] = formatMoney(filteredData.reduce((acc, row) => acc + parseMoney(row[h]), 0));
    }
  });

  const { exportingText, runExport } = useReportExport();

  const downloadPDF = () => {
    runExport("Generating PDF Report...", () => {
      const doc = new jsPDF();
      doc.text("Group List Of", 14, 15);

      const tableColumn = headers.map(h => h === 'DrCr' ? 'Dr/Cr' : h === 'OPBalance' ? 'Op. Balance' : h);
      const tableRows = [];

      filteredData.forEach((row) => {
        const rowData = headers.map(h => {
          const val = row[h];
          if (typeof val === 'number') return formatMoney(val);
          const isRightAlign = ['OPBalance', 'Debit', 'Credit', 'Balance', 'Payment', 'Receipt', 'GroupAmount', 'Ledamount', 'Amount'].includes(h);
          if (isRightAlign && typeof val === 'string' && val !== "") return formatMoney(parseMoney(val));
          return val || "";
        });
        tableRows.push(rowData);
      });

      const totalRow = headers.map((h, i) => i === 0 ? "Total" : (totals[h] || ""));
      tableRows.push(totalRow);

      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 20,
      });

      doc.save("GroupList_Report.pdf");
    });
  };

  const downloadExcel = () => {
    runExport("Generating Excel / CSV...", () => {
      const headers = [
        "Name",
        "Address",
        "Op. Balance",
        "Debit",
        "Credit",
        "Balance",
        "Dr/Cr",
      ];

      let csvContent =
        "data:text/csv;charset=utf-8," +
        headers.join(",") +
        "\n" +
        filteredData
          .map((row) =>
            headers.map((h) => {
              const val = row[h];
              return typeof val === 'number' ? val : (val || "");
            }).join(",")
          )
          .join("\n");

      const csvTotalRow = headers.map((h, i) => i === 0 ? "Total" : (totals[h] || "").replace(/,/g, "")).join(",");
      csvContent += `\n${csvTotalRow}`;

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "GroupList_Report.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  const handlePrint = () => {
    window.print();
  };

  // Stable column resize handler (using ResizableTh defined at module scope)
  const handleColumnResize = useCallback((columnKey, newWidth) => {
    setColumnWidths((prev) => ({ ...prev, [columnKey]: newWidth }));
  }, []);

  return (
    <>
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .print-area, .print-area * {
              visibility: visible;
            }
            .print-area {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
            .no-print {
              display: none !important;
            }
          }
        `}
      </style>
      <div
        className="absolute inset-0 w-full h-full bg-white flex items-center justify-center pointer-events-auto"
        style={{ zIndex: zIndex || 101 }}
      >
        <div
          className="group-list-report-container bg-white font-['Inter'] w-[98%] h-[98%] rounded-[2px] border border-slate-300 shadow-2xl flex flex-col absolute overflow-hidden transition-all duration-300"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          <style>{`
            .group-list-report-container, .group-list-report-container * {
              font-family: 'Inter', sans-serif !important;
            }
            .report-table th {
              border-right: 1px solid #4c6ec4;
              border-bottom: 1px solid #4c6ec4;
            }
            .report-table td {
              border-right: 1px solid #d1d5db;
              border-bottom: 1px solid #d1d5db;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            .report-table th:last-child,
            .report-table td:last-child {
              border-right: none;
            }
            .report-table tbody tr:nth-child(odd) {
              background-color: #ffffff;
            }
            .report-table tbody tr:nth-child(even) {
              background-color: #f8fafc;
            }
            .report-table tbody tr:hover {
              background-color: #f1f5f9;
            }
          `}</style>
          <WindowHeader
            title="Group List Of"
            logo={headeLogo}
            onClose={onClose}
            closeBtnClassName="w-6 h-6 border-none rounded-[5px] mr-2 text-white bg-[#D32F2F] hover:bg-red-700"
          />
          <div className="flex flex-col h-full bg-[#f3f4f6]">
            <div className="no-print">
              <ActionToolbar
                showSave={false}
                showEdit={false}
                showDelete={false}
                showFind={false}
                showClear={false}
                showPrint={true}
                showPdf={true}
                showExcel={true}
                showDesign={true}
                showNavigation={false}
                showExit={true}
                onPrint={handlePrint}
                onPdf={downloadPDF}
                onExcel={downloadExcel}
                onDesign={() => {}}
                onExit={onClose}
                printIcon={FiPrinter}
                pdfIcon={FiFileText}
                excelIcon={FiFile}
                exitIcon={FiLogOut}
                designIcon={FiLayout}
              />
            </div>

            {/* Info Row */}
            <div className="flex justify-between items-center px-4 py-2 border-b border-gray-200 text-[#7a8ba6] text-[11px] font-bold">
              <span>Period: {payload?.fromDate || ""} – {payload?.toDate || ""}</span>
              <div className="flex items-center gap-4">
                <input
                  type="text"
                  placeholder="Global Search..."
                  className="px-2 py-1 border border-gray-300 rounded text-black font-normal outline-none focus:border-blue-400"
                  value={globalSearch}
                  onChange={(e) => setGlobalSearch(e.target.value)}
                />
                <span>Report Type: {payload?.reportType || ""}</span>
              </div>
            </div>

            <div
              className="flex-1 p-0 overflow-auto print-area outline-none focus:ring-1 focus:ring-blue-400"
              ref={tableContainerRef}
              tabIndex={0}
              onScroll={handleScroll}
              onWheel={handleWheel}
              onKeyDown={handleKeyDown}
            >
              <div className="bg-white">
                <table className="min-w-full table-fixed text-[10px] sm:text-[11px] text-left border-collapse font-medium text-[#1A237E] report-table">
                  <thead className="text-[11px] text-white bg-[#2b4c9b] font-bold sticky top-0 z-20">
                    <tr>
                      {headers.map((h, i) => {
                        const isRightAlign = ['OPBalance', 'Debit', 'Credit', 'Balance', 'Payment', 'Receipt', 'GroupAmount', 'Ledamount', 'Amount'].includes(h);
                        const isCenterAlign = ['DrCr', 'Type'].includes(h);
                        return (
                          <ResizableTh
                            key={i}
                            columnKey={h}
                            width={columnWidths[h]}
                            onResize={handleColumnResize}
                            className={`px-3 py-2 ${isRightAlign ? 'text-right' : isCenterAlign ? 'text-center' : 'text-left'}`}
                          >
                            {h === 'DrCr' ? 'Dr/Cr' : h === 'OPBalance' ? 'Op. Balance' : h}
                          </ResizableTh>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td
                          colSpan={headers.length}
                          className="px-3 py-4 text-center font-bold text-slate-500"
                        >
                          Loading data...
                        </td>
                      </tr>
                    ) : filteredData.length === 0 ? (
                      <tr>
                        <td
                          colSpan={headers.length}
                          className="px-3 py-4 text-center font-bold text-slate-500"
                        >
                          No data found for the selected filters.
                        </td>
                      </tr>
                    ) : (
                      displayedRows.map((row, index) => {
                        const isSelected = index === selectedIndex;
                        return (
                          <tr
                            key={index}
                            data-row-index={index}
                            onClick={() => setSelectedIndex(index)}
                            className={`cursor-pointer transition-colors ${
                              isSelected ? "!bg-blue-100 font-bold !text-blue-900 ring-1 ring-inset ring-blue-500" : ""
                            }`}
                          >
                            {headers.map((h, i) => {
                              const val = row[h];
                              const isRightAlign = ['OPBalance', 'Debit', 'Credit', 'Balance', 'Payment', 'Receipt', 'GroupAmount', 'Ledamount', 'Amount'].includes(h);
                              const isCenterAlign = ['DrCr', 'Type'].includes(h);
                              
                              let displayVal = val;
                              if (typeof val === 'number') {
                                 displayVal = formatMoney(val);
                              } else if (isRightAlign && typeof val === 'string' && val !== "") {
                                 displayVal = formatMoney(parseMoney(val));
                              }
                              
                              return (
                                <td key={i} className={`px-3 py-2 ${isRightAlign ? 'text-right font-medium text-[#424242]' : isCenterAlign ? 'text-center font-bold text-[#D32F2F]' : 'text-[#1A237E] font-bold'}`}>
                                  {displayVal || ""}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })
                    )}
                    {!loading && filteredData.length > 0 && (
                      <>
                        <tr
                          className="bg-[#f0f4f8] font-bold sticky bottom-0 z-20"
                          style={{ backgroundColor: "#f0f4f8", boxShadow: "0 -2px 5px rgba(0,0,0,0.05)" }}
                        >
                          {headers.map((h, i) => (
                            <td key={i} className={`px-3 py-2 text-[#1A237E] ${totals[h] ? 'text-right' : 'text-left'}`}>
                              {i === 0 ? "Total" : (totals[h] || "")}
                            </td>
                          ))}
                        </tr>
                        {/* Extra spacer row below the last row so it isn't cut off or covered */}
                        <tr>
                          <td colSpan={headers.length} className="h-8"></td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <TablePagination
              currentPage={currentPage}
              totalRows={totalRows}
              pageSize={pageSize}
              pageSizeOptions={pageSizeOptions}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              autoAdvance={autoAdvance}
              onToggleAutoAdvance={toggleAutoAdvance}
              isFlipping={isFlipping}
            />

            {/* Footer */}
            <div className="flex justify-between items-center px-4 py-1.5 bg-[#f3f4f6] text-[9px] font-bold text-[#9ca3af]">
              <span>SherGold ERP • Powered by SherSoft</span>
              <span>22/Jun/2026 12:17 PM</span>
            </div>
          </div>
        </div>
        {/* Global Export Loader for Heavy Operations */}
        <ExportLoader message={exportingText} />
      </div>
    </>
  );
};

export default GroupListReport;
